import React, { useEffect, useMemo, useRef, useState } from "react";

type QaTabId = "policy" | "qa" | "procedures" | "stockpiling" | "conclusion";

type QaTab = {
  id: QaTabId;
  label: string;
  kicker: string;
  title: string;
  accent: "red" | "amber";
  image?: { src: string; alt: string };
  body: React.ReactNode;
};

const Pill: React.FC<{
  active?: boolean;
  accent?: "red" | "amber";
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ active, accent = "red", children, onClick }) => {
  const activeCls =
    accent === "red"
      ? "bg-red-600 text-white border-red-600"
      : "bg-amber-500 text-white border-amber-500";

  const idleCls =
    "bg-white text-gray-800 border-gray-200 hover:bg-gray-50 hover:border-gray-300";

  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full border text-sm font-semibold transition w-full sm:w-auto ${active ? activeCls : idleCls
        }`}
      type="button"
    >
      {children}
    </button>
  );
};

/* =========================
   REVEAL ONCE (ANIMATE ONCE)
   - animation happens only the first time it enters viewport
   - after that, stays visible (no repeat)
   ========================= */
const RevealOnce: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: "fadeUp" | "fadeLeft" | "fadeRight" | "scale";
  delayMs?: number;
}> = ({ children, className = "", variant = "fadeUp", delayMs = 0 }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect(); // ✅ animate only once
        }
      },
      { threshold: 0.22, rootMargin: "0px 0px -15% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const base =
    "transition-all duration-700 ease-out will-change-transform will-change-opacity";
  const delay = delayMs ? ({ transitionDelay: `${delayMs}ms` } as React.CSSProperties) : undefined;

  const hiddenMap: Record<string, string> = {
    fadeUp: "opacity-0 translate-y-6",
    fadeLeft: "opacity-0 -translate-x-6",
    fadeRight: "opacity-0 translate-x-6",
    scale: "opacity-0 scale-95",
  };

  const show = "opacity-100 translate-y-0 translate-x-0 scale-100";

  return (
    <div
      ref={ref}
      style={delay}
      className={`${className} ${base} ${shown ? show : hiddenMap[variant]}`}
    >
      {children}
    </div>
  );
};

const QualityAssurance: React.FC<{ content?: any }> = ({ content }) => {
  const tabs: QaTab[] = useMemo(
    () => [
      {
        id: "policy",
        label: "Policy",
        kicker: content?.qa_policy_kicker || "Mining Production Control Policy",
        title: content?.qa_policy_title || "Purpose of the Quality Plan",
        accent: "red",
        image: { src: content?.qa_policy_image || "/gabbro/qa-policy.jpg", alt: content?.qa_policy_title || "Mining Production Control Policy" },
        body: (
          <>
            <div className="text-sm text-gray-700 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: (content?.qa_policy_body || `This Quality Plan is intended to serve as a guide to the appointed Production Manager, Quality Inspectors and associated personnel of the Quarry and Crusher Plant to ensure that the materials produced and supplied by the company meets the required standard and specifications of ‘Quality Assurance System’ which is in line with QCS 2014.<br/><br/>This Quality Plan may be modified according to a Qatar Construction Specification for Aggregates or as found necessary to maintain and improve, where possible, expected standards. The parties shall prepare and sign a supplement of revision to this schedule in the event such changes are deemed necessary.`).replace(/\n/g, '<br/>') }} />
          </>
        ),
      },
      {
        id: "qa",
        label: "QA System",
        kicker: content?.qa_system_kicker || "QUALITY ASSURANCE (QA) SYSTEM",
        title: content?.qa_system_title || "Quality Awareness & Organization Structure",
        accent: "amber",
        image: { src: content?.qa_system_image || "/gabbro/qa-system.jpg", alt: content?.qa_system_title || "Quality Assurance System" },
        body: (
          <>
            <h4 className="text-sm font-bold text-red-700">{content?.qa_system_awareness_title || "Quality Awareness"}</h4>
            <div className="mt-2 text-sm text-gray-700 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: (content?.qa_system_awareness_desc || `The management of Dar Bani Omar Trading & Contracting LLC is aware that quality is a process which is on-going and is an integral part of the production system.<br/><br/>Dar Bani Omar as an organization comprehends that the nature of the Business undertaken by it is such that the requirement of every customer is different. Keeping this in view, all practical steps are taken to produce material that complies with the contractual specifications as per QCS 2014.`).replace(/\n/g, '<br/>') }} />

            <h4 className="mt-5 text-sm font-bold text-red-700">{content?.qa_system_org_title || "Organization Structure"}</h4>
            <div className="mt-2 text-sm text-gray-700 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: (content?.qa_system_org_desc || `The quarry produces well graded crushed aggregates for concrete and road Operations are carried out throughout the week, up to 20 hours per day. We have designed a substantial, proactive QA system, which has been incorporated into all aspects of the production process. This ensures consistent product quality that is in line with QCS 2014.`).replace(/\n/g, '<br/>') }} />
          </>
        ),
      },
      {
        id: "procedures",
        label: "Procedures",
        kicker: content?.qa_procedures_kicker || "Quality Assurance Procedures",
        title: content?.qa_procedures_title || "Standards & Routine Controls",
        accent: "red",
        image: content?.qa_procedures_image ? { src: content?.qa_procedures_image, alt: content?.qa_procedures_title || "Quality Assurance Procedures" } : undefined,
        body: (
          <>
            <p className="text-sm text-gray-700 leading-relaxed text-justify">
              {content?.qa_procedures_intro || "At Dar Bani Omar Trading & Contracting LLC the QA procedures are based on the Crushed Aggregate and Sand. The QA standards and guidelines being followed are American Society for Testing and Materials (ASTM)."}
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl border bg-gray-50 p-5">
                <div className="text-base uppercase tracking-widest text-red-500 font-bold">Standards</div>
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                  {(content?.qa_standards ?? "• British Standards (BS EN)\n• Qatar Construction Specifications (QCS) 2014\n• ASTM (Testing & Materials)").split('\n').map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ul>
              </div>

              <div className="rounded-2xl border bg-gray-50 p-5">
                <div className="text-base uppercase tracking-widest text-red-500 font-bold">
                  Core QA Activities
                </div>
                <ul className="mt-3 space-y-2 text-sm text-gray-700 text-justify">
                  {(content?.qa_activities ?? "• Conduct routine visual inspection of aggregates produced every day by the Quality Inspector as guided by the QA Department under the guidance of Production Manager.\n• Test aggregate samples taken from production belt conveyors dropping point / stockpiles for relevant tests as per QCS 2014.\n• Aggregates are also tested at internationally recognized independent laboratories as per quarry schedule and / or QCS 2014.\n• Engage in discussions, subsequent to the receipt of test results, between the QA department and the Production department. Initiate corrective action if nonconformity material is found.\n• Liaison between the QA Department and the Production Department on a daily basis with regard to production methods, routine testing and status of stockpiles.\n• Monitor the material loaded from the stock onto the truck / barge / ship as checked by the QA Department / Production Manager.\n• Arrange for calibration and /in-house testing of measuring and test equipment as per schedule.\n• Prepare periodic Quality Control reports for the client as per contractual requirements.\n• Prepare an internal monthly report summarizing the activities of the QA department which is submitted to the General Manager.\n• Formulate a graph with standard deviation against specification in line with QCS 2014 to check the conformance of the material.").split('\n').map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            </div>
          </>
        ),
      },
      {
        id: "stockpiling",
        label: "Stockpiling",
        kicker: content?.qa_stockpiling_kicker || "STOCKPILING & DISPATCH QUALITY CONTROL",
        title: content?.qa_stockpiling_title || "Preparation, Sampling & Testing",
        accent: "amber",
        image: { src: content?.qa_stockpiling_image || "/gabbro/qa-stockpiling.jpg", alt: content?.qa_stockpiling_title || "Stockpiling & Dispatch Quality Control" },
        body: (
          <>
            <div className="text-sm text-gray-700 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: (content?.qa_stockpiling_body || `Stockyard Area Preparation: Production Manager along with QA department representative, identifies area for different graded aggregate stockpiles and label them with name board for recognition.<br/><br/>Then the stockpile area is cleaned, leveled and spread with 5mm and compacting the floor. The stockpiles are maintained distinctly and clean to minimize the risk of possible contamination.<br/><br/>The stockpiles are inspected, and representative samples taken from stockpiles are tested. Corrective action is taken on any material failing to meet the specifications. Besides the testing done by Quarry, an Independent laboratory is also engaged for sampling and testing of stockpiles as per QCS2014.`).replace(/\n/g, '<br/>') }} />
          </>
        ),
      },
      {
        id: "conclusion",
        label: "Conclusion",
        kicker: content?.qa_conclusion_kicker || "Closing Statement",
        title: content?.qa_conclusion_title || "Continuous Monitoring & Vision 2030",
        accent: "red",
        image: { src: content?.qa_conclusion_image || "/gabbro/qa-conclusion.png", alt: content?.qa_conclusion_title || "Conclusion / Partner Logos" },
        body: (
          <>
            <div className="text-sm text-gray-700 leading-relaxed text-justify" dangerouslySetInnerHTML={{ __html: (content?.qa_conclusion_body || `As evidenced by the foregoing, Hamdan International for Gabbro – Qatar strives to produce high quality materials that satisfy all its customers to distinguish it from others.<br/><br/>The system is designed to achieve this goal through continuous monitoring from the outputs from every aspect of the production system straight from the quarry front to customer centers directly.<br/><br/>It seeks to be a key partner in the vision of the State of Qatar to contribute to the future vision of 2030.<br/><br/>Continuous improvement and strategic supply are our core values.`).replace(/\n/g, '<br/>') }} />
          </>
        ),
      },
    ],
    [content]
  );

  const [active, setActive] = useState<QaTabId>("policy");
  const current = tabs.find((t) => t.id === active)!;

  return (
    <section className="bg-white py-10">
      <div className="max-w-7xl mx-auto px-4">
        {/* ===== HEADER – SPLIT CARD STYLE ===== */}
        <RevealOnce variant="fadeUp">
          <div className="rounded-3xl border bg-gray-50 p-8 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500">
                  {content?.qa_header_kicker || "Hamdan International for Gabbro – Qatar"}
                </div>
                <h1 className="mt-2 text-3xl font-bold text-gray-900">
                  {content?.qa_header_title || "Quality Assurance (QA)"}
                </h1>
                <p className="mt-3 max-w-3xl text-sm text-gray-600 leading-relaxed">
                  {content?.qa_header_desc || "This section outlines the Quality Assurance framework adopted at quarry and crusher plant operations to ensure compliance with QCS 2014 and international quality standards."}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="text-xs px-3 py-1 rounded-full border bg-yellow-400">QCS 2014</span>
                <span className="text-xs px-3 py-1 rounded-full border bg-yellow-400">ASTM</span>
                <span className="text-xs px-3 py-1 rounded-full border bg-yellow-400">BS EN</span>
              </div>
            </div>
          </div>
        </RevealOnce>

        {/* ===== TAB BUTTONS ===== */}
        <RevealOnce variant="fadeUp" delayMs={120}>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-3">
            {tabs.map((t) => (
              <Pill
                key={t.id}
                active={active === t.id}
                accent={t.accent}
                onClick={() => setActive(t.id)}
              >
                {t.label}
              </Pill>
            ))}
          </div>
        </RevealOnce>

        {/* ===== CONTENT PANEL ===== */}
        <RevealOnce variant="fadeUp" delayMs={220}>
          <div className="mt-8 rounded-3xl border bg-white shadow-sm overflow-hidden">
            {/* top label bar */}
            <div className="px-7 py-5 bg-gray-50 border-b">
              <div className="text-xs uppercase tracking-widest text-gray-500">
                {current.kicker}
              </div>
              <div className="mt-2 text-xl font-bold text-gray-900">
                {current.title}
              </div>
            </div>

            {/* body (text + image) */}
            <div className="p-7 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className={`${current.image ? "lg:col-span-7" : "lg:col-span-12"}`}>
                {current.body}
              </div>

              {current.image && (
                <div className="lg:col-span-5">
                  <div className="rounded-2xl border overflow-hidden bg-white-50">
                    <img
                      src={current.image.src}
                      alt={current.image.alt}
                      className="w-full h-[280px] object-contain"
                    />
                    <div className="p-4 bg-white">
                      <div className="mt-1 text-sm font-semibold text-gray-900">
                        {current.image.alt}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* bottom accent line */}
            <div className="h-1 w-full bg-gradient-to-r from-red-700 via-rose-500 to-amber-400" />
          </div>
        </RevealOnce>
      </div>
    </section>
  );
};

export default QualityAssurance;
